import {
  Box,
  Flex,
  Heading,
  Spacer,
  Text,
  Table,
  TableContainer,
  Tbody,
  Thead,
  Td,
  Tr,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { Receivable } from '@finley-technologies/finley-types';

import { useGetOrganizationLoanProductCreditFacilityGroupingsForAssignmentWithPipelineInfoQuery } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { AssignmentSkeletonLoadingTable } from '../AssignmentSkeletonLoadingTables';
import { TableHeader } from '../styles';
import { LpToCfEntityGroupingWithPipelineInfo } from '../types';
import { AssignmentMethodCell } from '../v2/AssignmentSummary/PreviewJobsTable';
import { colors } from 'packages/common-frontend/colors';
import { FiChevronRight } from 'react-icons/fi';

export const AssignmentSelectImplementationTable = ({
  implementations,
}: {
  implementations: LpToCfEntityGroupingWithPipelineInfo[];
}) => {
  const navigate = useNavigate();

  const getStatusText = (status: Receivable.Assignment.Pipeline.Status) => {
    switch (status) {
      case 'assigned':
        return 'Assignment Completed';
      case 'error':
        return 'Upload Error';
      case 'inProgress':
        return 'Tapes Processing';
      case 'incomplete':
        return 'Tapes Missing';
      case 'success':
        return 'Assignment Ready';
      case 'noPipelineRun':
        return 'Upload Tapes';
      default:
        return finleyAssertUnreachable(status);
    }
  };

  return (
    <Box mb="32px">
      <TableContainer borderWidth="1px">
        <Table>
          <Thead>
            <Tr>
              <TableHeader style={{ width: '30%' }} name="Loan Product" />
              <TableHeader style={{ width: '30%' }} name="Facility" />
              <TableHeader style={{ width: '15%' }} name="Status" />
              <TableHeader style={{ width: '20%' }} name="Latest Preview" />
              <TableHeader style={{ width: '5%' }} name="" />
            </Tr>
          </Thead>
          <Tbody>
            {implementations.length === 0 ? (
              <Tr>
                <Td>
                  <Text color="gray">
                    No loan product and credit facility group is available for
                    assignment.
                  </Text>
                </Td>
              </Tr>
            ) : (
              implementations.map((implementation) => {
                const {
                  loanProducts,
                  creditFacilities,
                  implementationId,
                  pipelineInfo,
                } = implementation;
                const loanProductsNames = loanProducts
                  .map((loanProduct) => loanProduct.name)
                  .join(', ');

                const creditFacilityNames = creditFacilities
                  .map((creditFacility) => creditFacility.name)
                  .join(', ');

                const { status, latestCompletedPreviewJob } = pipelineInfo;

                return (
                  <Tr
                    key={implementationId}
                    onClick={() => {
                      navigate(`/assignments/${implementationId}/summary`);
                    }}
                    _hover={{ background: '#F7F9F9', cursor: 'pointer' }}
                  >
                    <Td minWidth="20vw" whiteSpace="pre-wrap">
                      <Text color="gray">{loanProductsNames}</Text>
                    </Td>
                    <Td minWidth="20vw" whiteSpace="pre-wrap">
                      <Text color="gray">{creditFacilityNames}</Text>
                    </Td>
                    <Td>
                      <Text color="gray">
                        {getStatusText(status ?? 'noPipelineRun')}
                      </Text>
                    </Td>
                    <Td>
                      {latestCompletedPreviewJob ? (
                        <AssignmentMethodCell
                          assignmentMethod={
                            latestCompletedPreviewJob.assignmentMethod
                          }
                          strategyId={
                            latestCompletedPreviewJob.strategyId ?? null
                          }
                          strategyName={
                            latestCompletedPreviewJob.strategyName ?? null
                          }
                        />
                      ) : (
                        <Text color="gray">-</Text>
                      )}
                    </Td>
                    <Td>
                      <FiChevronRight color={colors.blue[400]} />
                    </Td>
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export const AssignmentSelectImplementationContainer = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const navigate = useNavigate();

  const {
    data: groupings,
    loading: groupingsLoading,
  } = useGetOrganizationLoanProductCreditFacilityGroupingsForAssignmentWithPipelineInfoQuery(
    {
      variables: { organizationId },
    },
  );

  useEffect(() => {
    if (
      groupings &&
      groupings.organization.loanProductCreditFacilityGroupsForAssignment
        .length === 1
    ) {
      const implementationId =
        groupings.organization.loanProductCreditFacilityGroupsForAssignment[0]
          .implementationId;
      navigate(`/assignments/${implementationId}/summary`);
    }
  }, [groupings, navigate]);

  return (
    <Box height="100%">
      {(() => {
        if (!groupings || groupingsLoading) {
          return (
            <Flex direction="column">
              <Flex justify="space-between" mb="24px">
                <Heading as="h2">Assignments</Heading>
                <Spacer />
              </Flex>
              <AssignmentSkeletonLoadingTable
                numberOfRows={5}
                numberOfColumns={4}
              />
            </Flex>
          );
        }

        return (
          <Box>
            <Flex justify="space-between" mb="24px">
              <Heading as="h2">Assignments</Heading>
              <Spacer />
            </Flex>
            {groupings.organization.loanProductCreditFacilityGroupsForAssignment
              .length === 1 ? (
              <AssignmentSkeletonLoadingTable
                numberOfRows={5}
                numberOfColumns={4}
              />
            ) : (
              <>
                <Text fontSize="lg" mb="32px" color="gray">
                  Select the facilities to assign receivables.
                </Text>
                <AssignmentSelectImplementationTable
                  implementations={
                    groupings.organization
                      .loanProductCreditFacilityGroupsForAssignment
                  }
                />
              </>
            )}
          </Box>
        );
      })()}
    </Box>
  );
};
